import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type {
  SecurityAlertRow,
  SecurityDashboard,
  SecurityDashboardFilters as SecurityDashboardFilterValues,
} from '../../../models/securityDashboard';
import { fetchSecurityAlerts, fetchSecurityDashboard } from '../../../services/admin/securityDashboardService';
import { fetchAdminDashboard } from '../../../services/admin/dashboardService';
import { SecurityDashboardFilters } from '../components/SecurityDashboardFilters';
import { SecurityMetricCards, type SecurityMetricCard } from '../components/SecurityMetricCards';
import { SecurityAlertsPanel } from '../components/SecurityAlertsPanel';
import type { AuditCompanyOption } from '../components/AuditFiltersPanel';

/** Recent-alerts page size for the dashboard's alerts panel — a small fixed-length list, not a paged grid. */
const ALERTS_PAGE_SIZE = 50;

type Loadable<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string };

export function SecurityDashboardPage(): React.ReactElement {
  useDocumentTitle('Security · Triplicity Backoffice');
  const { getAccessToken, acquireAccessToken } = useAuth();

  const [metrics, setMetrics] = useState<Loadable<SecurityDashboard>>({ status: 'loading' });
  const [alerts, setAlerts] = useState<Loadable<SecurityAlertRow[]>>({ status: 'loading' });
  const [companies, setCompanies] = useState<AuditCompanyOption[]>([]);
  const [filters, setFilters] = useState<SecurityDashboardFilterValues>({});
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(async (): Promise<void> => {
    setMetrics({ status: 'loading' });
    setAlerts({ status: 'loading' });

    const [d, a] = await Promise.allSettled([
      fetchSecurityDashboard(getAccessToken, filters),
      fetchSecurityAlerts(getAccessToken, filters, 'active', 1, ALERTS_PAGE_SIZE),
    ]);

    if (d.status === 'fulfilled') {
      setMetrics({ status: 'ready', data: d.value });
    } else {
      setMetrics({
        status: 'error',
        message: d.reason instanceof Error ? d.reason.message : 'Failed to load security metrics.',
      });
    }

    if (a.status === 'fulfilled') {
      setAlerts({ status: 'ready', data: a.value.items });
    } else {
      setAlerts({
        status: 'error',
        message: a.reason instanceof Error ? a.reason.message : 'Failed to load security alerts.',
      });
    }
    // `reloadToken` is not read in the body — it is a deliberate dependency so the Refresh button
    // (which only bumps this counter) produces a new callback identity and re-runs the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessToken, filters, reloadToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Company options for the filter picker (reuses the dashboard company list, same as the other security
  // screens). Failures here must never block or affect the metrics/alerts sections.
  useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const companyRows = await fetchAdminDashboard(acquireAccessToken);
        if (isMounted) {
          setCompanies(companyRows.map((c) => ({ companyId: c.companyId, companyName: c.companyName })));
        }
      } catch {
        if (isMounted) {
          setCompanies([]);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [acquireAccessToken]);

  const isRefreshing = metrics.status === 'loading' || alerts.status === 'loading';

  const metricCards: SecurityMetricCard[] =
    metrics.status === 'ready'
      ? [
          { label: 'Failed logins', value: metrics.data.totals.failedLogins },
          {
            label: 'Unauthorized API calls',
            value: metrics.data.totals.unauthorizedApiCalls,
            to: '/admin/security/api-activity',
          },
          {
            label: 'Suspicious downloads',
            value: metrics.data.totals.massDownloadAlerts,
            to: '/admin/security/download-alerts',
            title: 'Count of mass-download security alerts raised in this window, not raw download volume.',
          },
          { label: 'Active alerts', value: metrics.data.totals.activeAlerts },
          { label: 'Lockouts', value: metrics.data.totals.lockouts },
          { label: 'Suspicious logins', value: metrics.data.totals.suspiciousLogins },
          { label: 'Sensitive-route hits', value: metrics.data.totals.sensitiveRouteHits },
        ]
      : [];

  const allTotalsZero = metrics.status === 'ready' && Object.values(metrics.data.totals).every((v) => v === 0);

  return (
    <div>
      <PageHeader
        title="Security"
        subtitle="Overview of authentication failures, denied API access, and data-access alerts across tenants."
        actions={
          <button
            type="button"
            className="bo-btn bo-btn-secondary"
            onClick={() => setReloadToken((t) => t + 1)}
            disabled={isRefreshing}
          >
            Refresh
          </button>
        }
      />

      <SecurityDashboardFilters filters={filters} companies={companies} onChange={setFilters} />

      {metrics.status === 'loading' ? <Spinner label="Loading security metrics…" /> : null}
      {metrics.status === 'error' ? <ErrorAlert message={metrics.message} /> : null}
      {metrics.status === 'ready' && allTotalsZero ? (
        <EmptyState
          title="No security activity"
          description="No failed logins, denied API calls, or alerts in this window."
        />
      ) : null}
      {metrics.status === 'ready' && !allTotalsZero ? <SecurityMetricCards cards={metricCards} /> : null}

      {alerts.status === 'loading' ? <Spinner label="Loading security alerts…" /> : null}
      {alerts.status === 'error' ? <ErrorAlert message={alerts.message} /> : null}
      {alerts.status === 'ready' && alerts.data.length === 0 ? (
        <EmptyState title="No active alerts" description="No active security alerts match the current filters." />
      ) : null}
      {alerts.status === 'ready' && alerts.data.length > 0 ? <SecurityAlertsPanel alerts={alerts.data} /> : null}

      <div className="bo-actions">
        <Link to="/admin/security/api-activity">View API activity →</Link>
        <Link to="/admin/security/download-alerts">View download alerts →</Link>
      </div>
    </div>
  );
}
